import { BuildProcess } from '@App/app/entities/processing/build-process.model';
import { loadAllProcesses } from '@App/app/pages/processing/store/processes/actions/processes.actions';
import {
  selectAllProcesses,
  selectProcessesByArchived,
} from '@App/app/pages/processing/store/processes/selectors/processes.selectors';
import { untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { switchMap, take, withLatestFrom } from 'rxjs/operators';
import { ProcessesSitesService } from '../../shared/site-services/processes-sites.service';
import type { ProcessingTableComponent } from './processing-table.component';

interface ProcessLoadingHandlers {
  store: Store;
  sitesService: ProcessesSitesService | null;
  timer$: Observable<number>;
  setEntities: (processes: BuildProcess[]) => void;
  setLoading: (loading: boolean) => void;
  setSelectedRows: (rows: BuildProcess[]) => void;
}

/**
 * Wires up the store/timer/site-service subscriptions that feed the processing table.
 * Extracted from the component's ngOnInit to keep the component focused on view logic.
 */
export const initProcessLoadingSubscriptions = (
  ctx: ProcessingTableComponent,
  handlers: ProcessLoadingHandlers,
): void => {
  const { store, sitesService, timer$, setEntities, setLoading, setSelectedRows } = handlers;
  if (sitesService) {
    store
      .select(selectAllProcesses)
      .pipe(untilDestroyed(ctx), withLatestFrom(sitesService.showArchived$))
      .subscribe(([buildProcesses, archived]) =>
        setEntities(buildProcesses.filter((item) => item.isArchived === archived)),
      );
  }
  sitesService?.showArchived$.pipe(untilDestroyed(ctx)).subscribe((showArchived) => {
    store
      .select(selectProcessesByArchived(showArchived))
      .pipe(take(1))
      .subscribe((buildProcesses) => setEntities(buildProcesses));
  });

  timer$
    .pipe(
      untilDestroyed(ctx),
      switchMap((val) => {
        setLoading(true);
        store.dispatch(loadAllProcesses());
        return of(val);
      }),
    )
    .subscribe();

  sitesService?.selectedEntities.pipe(untilDestroyed(ctx)).subscribe((processes) => {
    setSelectedRows([...processes]);
  });
};
